import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { fmtLocalDate } from '../../utils/datetime';

// Sample In/Out Log — READ-ONLY custody history (ALPACA SampleIn_ListView): every
// check-in / check-out / disposal event. Mode filter chips + search. No editing.
interface LogRow {
  _id:            number;
  jobId:          string | null;
  sampleBottleId: string | null;
  from:           string | null;
  to:             string | null;
  mode:           string | null;
  dateHandled:    string | null;
  handledBy:      string | null;
  client:         string | null;
  storage:        string | null;
  user:           string | null;
}

type ModeFilter = 'All' | 'In' | 'Out' | 'Disposal';

const modeVariant = (m: string | null) => {
  const s = (m ?? '').toLowerCase();
  if (s === 'in') return 'ok';
  if (s === 'out') return 'info';
  if (s === 'disposed' || s === 'depleted') return 'alert';
  return 'warn';
};

export default function SampleInOutLogPage() {
  const [rows,    setRows]    = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs,  setLoadMs]  = useState<number | null>(null);
  const [error,   setError]   = useState<string | null>(null);
  const [modeF,   setModeF]   = useState<ModeFilter>('All');
  const [search,  setSearch]  = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const res = await api.get('/api/v1/sample-custody/log');
      const data = (res.data as { data: Omit<LogRow, '_id'>[] }).data ?? [];
      setRows(data.map((r, i) => ({ ...r, _id: i })));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Mode chip counts (Disposal = Disposed + Depleted).
  const counts = useMemo(() => {
    const c = { All: rows.length, In: 0, Out: 0, Disposal: 0 };
    for (const r of rows) {
      const m = (r.mode ?? '').toLowerCase();
      if (m === 'in') c.In++;
      else if (m === 'out') c.Out++;
      else if (m === 'disposed' || m === 'depleted') c.Disposal++;
    }
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    let r = rows;
    if (modeF !== 'All') {
      r = r.filter(x => {
        const m = (x.mode ?? '').toLowerCase();
        if (modeF === 'Disposal') return m === 'disposed' || m === 'depleted';
        return m === modeF.toLowerCase();
      });
    }
    if (search.trim()) {
      const t = search.toLowerCase();
      r = r.filter(x =>
        [x.jobId, x.sampleBottleId, x.from, x.to, x.mode, x.handledBy, x.client, x.storage, x.user]
          .some(v => (v ?? '').toLowerCase().includes(t)));
    }
    return r;
  }, [rows, modeF, search]);

  const [visibleRows, setVisibleRows] = useState<LogRow[]>(filtered);

  const cols: AppColumn<LogRow>[] = useMemo(() => [
    { field: 'jobId',          header: 'JobID',          width: 110, text: r => r.jobId ?? '' },
    { field: 'sampleBottleId', header: 'SampleBottleID', width: 160, text: r => r.sampleBottleId ?? '' },
    { field: 'from',           header: 'From',           width: 140, text: r => r.from ?? '' },
    { field: 'to',             header: 'To',             width: 140, text: r => r.to ?? '' },
    { field: 'mode',           header: 'Mode', width: 110, render: r => <span className={`bl-pill ${modeVariant(r.mode)}`}>{r.mode}</span>, text: r => r.mode ?? '' },
    { field: 'dateHandled',    header: 'DateHandled',    width: 160, text: r => (r.dateHandled ? fmtLocalDate(r.dateHandled) : '') },   // date-only (ALPACA parity, §14b.6)
    { field: 'handledBy',      header: 'HandledBy',      width: 120, text: r => r.handledBy ?? '' },
    { field: 'client',         header: 'Client',         width: 200, text: r => r.client ?? '' },
    { field: 'storage',        header: 'Storage',        width: 110, text: r => r.storage ?? '' },
    { field: 'user',           header: 'User',           width: 110, text: r => r.user ?? '' },
  ], []);

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Sample Tracking</span><i className="ti ti-chevron-right" />
            <span>Barcode Sample Custody</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Sample In/Out Log</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="sample-inout-log" loadMs={loadMs} loading={loading} />
          </div>
          <div className="dp-subtitle">
            Read-only custody history — every check-in, check-out and disposal event · {loading ? '…' : rows.length} items
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            onRefresh={load}
            refreshing={loading}
            exportData={{ filename: 'SampleInOut', ...gridExportMatrix(visibleRows, cols) }}
          />
        </div>

        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input type="text" placeholder="Search sample, location, user…" value={search} onChange={e => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
        </div>

        <div className="bl-card bl-card-flex">
          <div className="sa-filter-row">
            <span className="sa-filter-lbl">Mode:</span>
            {(['All', 'In', 'Out', 'Disposal'] as ModeFilter[]).map(m => (
              <button key={m} className={`sa-filter-btn${modeF === m ? ' active' : ''}`} onClick={() => setModeF(m)}>
                {m} ({counts[m]})
              </button>
            ))}
          </div>

          {error ? (
            <div className="bl-err" style={{ margin: 12 }}>
              <i className="ti ti-alert-circle" /> {error}
              <button onClick={load} style={{ marginLeft: 10, color: 'var(--brand-accent, #e07b39)', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
            </div>
          ) : (
            <AppDataGrid<LogRow>
              rows={filtered}
              columns={cols}
              getRowId={r => r._id}
              loading={loading}
              loadMs={loadMs}
              onVisibleRowsChange={setVisibleRows}
              emptyText="No custody events found"
            />
          )}
        </div>
      </div>
    </div>
  );
}
